import React, { useEffect, useState } from "react";
import { staffApi, staffErr } from "./staffApi";
import { toast } from "sonner";
import "./staff.css";
import {
  Home, Calendar, Clock, MessageSquare, LogOut, Send, AlertTriangle, X, Bell,
  CalendarDays, UserX, Wallet, Download, WifiOff,
} from "lucide-react";

const N = (n) => "₦" + Number(n || 0).toLocaleString("en-NG",
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---------- Root ----------
export default function StaffPortal() {
  const [token, setToken] = useState(() => localStorage.getItem("staff_token"));
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("home");
  const [err, setErr] = useState("");
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [installEvt, setInstallEvt] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("staff_install_dismissed") === "1");

  // Register service worker + PWA install prompt
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/staff-sw.js", { scope: "/staff" }).catch(() => {});
    }
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const beforeInstall = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", beforeInstall);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("beforeinstallprompt", beforeInstall);
    };
  }, []);

  useEffect(() => {
    if (!token) { setMe(null); return; }
    staffApi.get("/staff/me").then(({ data }) => setMe(data))
      .catch((e) => { setErr(staffErr(e)); localStorage.removeItem("staff_token"); setToken(null); });
  }, [token]);

  const loadNotifs = () => staffApi.get("/staff/notifications").then(({ data }) => setNotifs(data || [])).catch(() => {});
  useEffect(() => {
    if (!me) return; loadNotifs();
    const iv = setInterval(loadNotifs, 15000); return () => clearInterval(iv);
  }, [me]);

  const unread = notifs.filter((n) => !n.read).length;
  const markAllRead = async () => { await staffApi.post("/staff/notifications/mark-all-read").catch(() => {}); loadNotifs(); };
  const markOne = async (nid) => { await staffApi.post(`/staff/notifications/${nid}/read`).catch(() => {}); loadNotifs(); };

  const logout = () => { localStorage.removeItem("staff_token"); setToken(null); setMe(null); setTab("home"); };

  const installApp = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    const choice = await installEvt.userChoice;
    if (choice.outcome === "accepted") {
      localStorage.setItem("staff_install_dismissed", "1");
      setInstallDismissed(true);
    }
    setInstallEvt(null);
  };

  if (!token || !me) return <Login onDone={(t) => { localStorage.setItem("staff_token", t); setToken(t); }} err={err} />;
  if (me.must_reset_password) return <ForcePasswordChange me={me} onDone={() => setMe({ ...me, must_reset_password: false })} onLogout={logout} />;

  const tabs = [
    ["home", "Dashboard", Home],
    ["schedule", "Schedule", CalendarDays],
    ["leave", "Leave", Calendar],
    ["lateness", "Late/Absent", Clock],
    ["credit", "My Credit", Wallet],
    ["queries", "Queries", MessageSquare],
  ];

  return (
    <div className="sm-app" data-testid="staff-portal">
      {!online && <div className="sm-offline" data-testid="staff-offline"><WifiOff size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />OFFLINE — showing cached data</div>}

      <header className="sm-topbar">
        <div className="sm-topbar-brand">
          <img src="/logo.jpg" alt="Sir Monty" />
          <div className="sm-topbar-brand-text">
            <div className="sm-topbar-title">Sir Monty Staff</div>
            <div className="sm-topbar-sub">{me.name} · {me.branch || "—"}</div>
          </div>
        </div>
        <div className="sm-topbar-actions">
          <button className="sm-icon-btn" onClick={() => setShowNotifs(true)} data-testid="staff-bell" aria-label="Notifications">
            <Bell size={16} />
            {unread > 0 && <span className="sm-badge" data-testid="staff-bell-badge">{unread}</span>}
          </button>
          <button className="sm-icon-btn" onClick={logout} data-testid="staff-logout" aria-label="Sign out"><LogOut size={16} /></button>
        </div>
      </header>

      <nav className="sm-tabs" role="tablist">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} className={`sm-tab ${tab === k ? "active" : ""}`}
                  onClick={() => setTab(k)} data-testid={`staff-tab-${k}`} role="tab" aria-selected={tab === k}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </nav>

      <main className="sm-main">
        {tab === "home" && <Dashboard me={me} />}
        {tab === "schedule" && <ScheduleSection />}
        {tab === "leave" && <LeaveSection me={me} />}
        {tab === "lateness" && <LatenessAbsence me={me} />}
        {tab === "credit" && <CreditSection />}
        {tab === "queries" && <QueriesSection />}
      </main>

      {installEvt && !installDismissed && (
        <div className="sm-install" data-testid="pwa-install-banner">
          <Download size={18} />
          <div className="sm-install-body">Install Sir Monty Staff on your phone for a native-app feel.</div>
          <button className="sm-install-btn" onClick={installApp} data-testid="pwa-install-btn">Install</button>
          <button className="sm-install-close" onClick={() => { setInstallDismissed(true); localStorage.setItem("staff_install_dismissed", "1"); }} aria-label="Dismiss"><X size={16} /></button>
        </div>
      )}

      {showNotifs && (
        <NotifPanel notifs={notifs} unread={unread} onClose={() => setShowNotifs(false)}
                    onMarkOne={markOne} onMarkAll={markAllRead} />
      )}
    </div>
  );
}

// ---------- Forced first-login password change ----------
function ForcePasswordChange({ me, onDone, onLogout }) {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (p1.length < 8) return toast.error("Use at least 8 characters");
    if (p1 !== p2) return toast.error("Passwords do not match");
    setBusy(true);
    try { await staffApi.put("/auth/profile", { password: p1 }); toast.success("Password updated"); onDone(); }
    catch (e) { toast.error(staffErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="sm-login" data-testid="staff-force-password">
      <div className="sm-login-card">
        <img src="/logo.jpg" alt="Sir Monty" style={{ height: 40, marginBottom: 10 }} />
        <div style={{ fontSize: 20, fontWeight: 800, color: "var(--sm-crimson)" }}>Set your password</div>
        <div style={{ fontSize: 12, color: "var(--sm-muted)", marginBottom: 18 }}>
          Hi {me.name} — you signed in with a temporary password issued by HR. Choose your own before you continue.
        </div>
        <label style={{ fontSize: 11, color: "var(--sm-muted)" }}>New password</label>
        <input className="sm-input" style={{ marginTop: 4, marginBottom: 10 }} type="password" value={p1}
               onChange={(e) => setP1(e.target.value)} data-testid="staff-new-password" />
        <label style={{ fontSize: 11, color: "var(--sm-muted)" }}>Confirm password</label>
        <input className="sm-input" style={{ marginTop: 4 }} type="password" value={p2}
               onKeyDown={(e) => e.key === "Enter" && submit()}
               onChange={(e) => setP2(e.target.value)} data-testid="staff-confirm-password" />
        <button className="sm-btn sm-btn-primary" style={{ marginTop: 16 }} onClick={submit}
                disabled={busy || !p1 || !p2} data-testid="staff-save-password">
          {busy ? "SAVING…" : "SAVE PASSWORD"}
        </button>
        <button className="sm-btn" style={{ marginTop: 8 }} onClick={onLogout} data-testid="staff-cancel-password">SIGN OUT</button>
      </div>
    </div>
  );
}

// ---------- Forgot password (code read out by HR) ----------
function ForgotPassword({ onBack }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [p1, setP1] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const request = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await staffApi.post("/auth/portal/forgot", { email });
      setMsg(r.data.message); setStep(2);
    } catch (e) { setMsg(staffErr(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (p1.length < 8) { setMsg("Use at least 8 characters"); return; }
    setBusy(true); setMsg("");
    try {
      await staffApi.post("/auth/portal/reset", { email, code, new_password: p1 });
      toast.success("Password updated — sign in with your new password");
      onBack();
    } catch (e) { setMsg(staffErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="sm-login" data-testid="staff-forgot">
      <div className="sm-login-card">
        <img src="/logo.jpg" alt="Sir Monty" style={{ height: 40, marginBottom: 10 }} />
        <div style={{ fontSize: 20, fontWeight: 800, color: "var(--sm-crimson)" }}>Forgot password</div>
        <div style={{ fontSize: 12, color: "var(--sm-muted)", marginBottom: 18 }}>
          {step === 1
            ? "Enter your staff email. HR will be given a 6-digit code to read out to you."
            : "Ask HR for your 6-digit code, then set a new password. The code lasts 30 minutes."}
        </div>

        <label style={{ fontSize: 11, color: "var(--sm-muted)" }}>Email</label>
        <input className="sm-input" style={{ marginTop: 4, marginBottom: 10 }} value={email} inputMode="email"
               autoCapitalize="off" autoCorrect="off" disabled={step === 2}
               onChange={(e) => setEmail(e.target.value)} data-testid="staff-forgot-email" />

        {step === 2 && (<>
          <label style={{ fontSize: 11, color: "var(--sm-muted)" }}>6-digit code from HR</label>
          <input className="sm-input" style={{ marginTop: 4, marginBottom: 10, letterSpacing: 4 }} value={code}
                 inputMode="numeric" maxLength={6} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                 data-testid="staff-forgot-code" />
          <label style={{ fontSize: 11, color: "var(--sm-muted)" }}>New password</label>
          <input className="sm-input" style={{ marginTop: 4 }} type="password" value={p1}
                 onKeyDown={(e) => e.key === "Enter" && reset()}
                 onChange={(e) => setP1(e.target.value)} data-testid="staff-forgot-password" />
        </>)}

        {msg && <div style={{ fontSize: 12, marginTop: 10, color: "var(--sm-muted)" }} data-testid="staff-forgot-msg">{msg}</div>}

        {step === 1 ? (
          <button className="sm-btn sm-btn-primary" style={{ marginTop: 16 }} onClick={request}
                  disabled={busy || !email} data-testid="staff-forgot-request">
            {busy ? "REQUESTING…" : "REQUEST CODE"}
          </button>
        ) : (
          <button className="sm-btn sm-btn-primary" style={{ marginTop: 16 }} onClick={reset}
                  disabled={busy || code.length !== 6 || !p1} data-testid="staff-forgot-submit">
            {busy ? "SAVING…" : "SET NEW PASSWORD"}
          </button>
        )}
        <button className="sm-btn" style={{ marginTop: 8 }} onClick={onBack} data-testid="staff-forgot-back">BACK TO SIGN IN</button>
      </div>
    </div>
  );
}

// ---------- Login ----------
function Login({ onDone, err }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(err || "");
  const [forgot, setForgot] = useState(false);
  const submit = async () => {
    setBusy(true); setError("");
    try { const r = await staffApi.post("/auth/login", { email, password }); onDone(r.data.token); }
    catch (e) { setError(staffErr(e)); } finally { setBusy(false); }
  };
  if (forgot) return <ForgotPassword onBack={() => setForgot(false)} />;
  return (
    <div className="sm-login" data-testid="staff-login">
      <div className="sm-login-card">
        <img src="/logo.jpg" alt="Sir Monty" style={{ height: 40, marginBottom: 10 }} />
        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--sm-crimson)" }}>Staff Portal</div>
        <div style={{ fontSize: 12, color: "var(--sm-muted)", marginBottom: 18 }}>Sign in with your Sir Monty staff account.</div>
        <label style={{ fontSize: 11, color: "var(--sm-muted)" }}>Email</label>
        <input className="sm-input" style={{ marginTop: 4, marginBottom: 10 }} value={email} inputMode="email" autoCapitalize="off" autoCorrect="off"
               onChange={(e) => setEmail(e.target.value)} data-testid="staff-email" />
        <label style={{ fontSize: 11, color: "var(--sm-muted)" }}>Password</label>
        <input className="sm-input" style={{ marginTop: 4 }} type="password" value={password}
               onKeyDown={(e) => e.key === "Enter" && submit()}
               onChange={(e) => setPassword(e.target.value)} data-testid="staff-password" />
        {error && <div style={{ color: "var(--sm-error)", fontSize: 12, marginTop: 10 }} data-testid="staff-login-err">{error}</div>}
        <button className="sm-btn sm-btn-primary" style={{ marginTop: 16 }} onClick={submit} disabled={busy || !email || !password} data-testid="staff-login-btn">
          {busy ? "SIGNING IN…" : "SIGN IN"}
        </button>
        <button onClick={() => setForgot(true)} data-testid="staff-forgot-link"
                style={{ marginTop: 12, background: "none", border: "none", fontSize: 12, fontWeight: 700, color: "var(--sm-crimson)", cursor: "pointer" }}>
          Forgot password?
        </button>
      </div>
    </div>
  );
}

// ---------- Shared UI ----------
const Card = ({ title, children, testid, action, hint }) => (
  <section className="sm-card" data-testid={testid}>
    <div className="sm-card-head">
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span className="sm-card-title">{title}</span>
        {hint && <span className="sm-card-hint">{hint}</span>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Stat = ({ label, value, accent }) => (
  <div className="sm-stat">
    <div className="sm-stat-label">{label}</div>
    <div className="sm-stat-value" style={{ color: accent }}>{value}</div>
  </div>
);

// ---------- Sections ----------
function Dashboard({ me }) {
  const [d, setD] = useState(null);
  useEffect(() => { staffApi.get("/staff/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <div style={{ padding: 30, textAlign: "center", color: "var(--sm-muted)" }}>Loading…</div>;
  return (
    <>
      <Card title="Profile" testid="staff-profile">
        <div className="sm-grid sm-grid-2">
          {[["Name", me.name], ["Role", me.role_name || "—"], ["Branch", me.branch || "—"], ["Department", me.department || "—"]].map(([l, v]) => (
            <div key={l}><div style={{ fontSize: 11, color: "var(--sm-muted)" }}>{l}</div><div style={{ fontSize: 14, fontWeight: 700 }}>{v}</div></div>
          ))}
        </div>
      </Card>

      {d.next_shift && (
        <Card title="Next shift" testid="staff-next-shift">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "var(--sm-crimson)" }}>{d.next_shift.date}</div>
              <div style={{ fontSize: 13, color: "var(--sm-muted)" }}>{d.next_shift.shift_name || "Shift"} · {d.next_shift.start_time || "—"} → {d.next_shift.end_time || "—"}</div>
            </div>
            <CalendarDays size={36} color="var(--sm-gold)" />
          </div>
        </Card>
      )}

      <div className="sm-grid sm-grid-4" style={{ marginBottom: 16 }}>
        <Stat label="Present days" value={d.attendance_this_month.present} accent="var(--sm-success)" />
        <Stat label="Late days" value={d.attendance_this_month.late} accent="var(--sm-gold)" />
        <Stat label="Absent days" value={d.attendance_this_month.absent} accent="var(--sm-error)" />
        <Stat label="Outstanding credit" value={N(d.credit_balance)} accent="var(--sm-crimson)" />
      </div>

      <Card title="Leave balances" testid="staff-leave-balances">
        {d.leave_balances.map((b) => (
          <div key={b.leave_type_id} className="sm-row">
            <div>
              <div style={{ fontWeight: 700 }}>{b.name}</div>
              <div style={{ fontSize: 11, color: "var(--sm-muted)" }}>Entitlement {b.entitlement} · Taken {b.taken}</div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--sm-crimson)" }}>{b.balance}</div>
          </div>
        ))}
      </Card>
    </>
  );
}

function ScheduleSection() {
  const [rows, setRows] = useState([]);
  useEffect(() => { staffApi.get("/staff/schedule", { params: { days: 21 } }).then(({ data }) => setRows(data || [])); }, []);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Card title="My upcoming schedule" hint="Next 3 weeks" testid="staff-schedule">
      {rows.length === 0 ? <div style={{ color: "var(--sm-muted)", fontSize: 13 }}>No upcoming shifts on the roster yet.</div>
        : rows.map((r) => {
          const isToday = r.date === today;
          const cancelled = r.status === "cancelled";
          return (
            <div key={r.id} className="sm-row" data-testid={`sched-row-${r.date}`} style={{ opacity: cancelled ? .5 : 1 }}>
              <div>
                <div style={{ fontWeight: 700, color: isToday ? "var(--sm-crimson)" : "var(--sm-text)" }}>
                  {new Date(r.date + "T00:00:00").toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "short" })}
                  {isToday && <span className="sm-chip sm-chip-today" style={{ marginLeft: 8 }}>TODAY</span>}
                </div>
                <div style={{ fontSize: 12, color: "var(--sm-muted)", marginTop: 2 }}>{r.shift_name || "Shift"} · {r.start_time || "—"} → {r.end_time || "—"}</div>
              </div>
              <span className={`sm-chip ${cancelled ? "sm-chip-late" : "sm-chip-ok"}`}>{r.status || "scheduled"}</span>
            </div>
          );
        })}
    </Card>
  );
}

function LeaveSection({ me }) {
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ leave_type_id: "", date_from: "", date_to: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const load = () => {
    staffApi.get("/staff/leaves").then(({ data }) => setRows(data));
    staffApi.get("/staff/leave-types").then(({ data }) => setTypes(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const submit = async () => {
    if (!form.leave_type_id || !form.date_from || !form.date_to) return toast.error("Fill leave type + dates");
    setBusy(true);
    try {
      await staffApi.post("/hr/leave-requests", { employee_id: me.employee_id, ...form });
      toast.success("Leave request submitted");
      setForm({ leave_type_id: "", date_from: "", date_to: "", reason: "" }); load();
    } catch (e) { toast.error(staffErr(e)); } finally { setBusy(false); }
  };
  return (<>
    <Card title="Submit leave request" testid="staff-leave-form">
      <div className="sm-grid sm-grid-1" style={{ gap: 10 }}>
        <select className="sm-select" value={form.leave_type_id} onChange={(e) => setForm({ ...form, leave_type_id: e.target.value })} data-testid="lv-type">
          <option value="">Leave type…</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.days_per_year}d)</option>)}
        </select>
        <div className="sm-grid sm-grid-2" style={{ gap: 10 }}>
          <input className="sm-input" type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="lv-from" />
          <input className="sm-input" type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="lv-to" />
        </div>
        <textarea className="sm-textarea" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  data-testid="lv-reason" placeholder="Reason (optional)" rows={2} />
        <button className="sm-btn sm-btn-primary" onClick={submit} disabled={busy} data-testid="lv-submit">
          <Send size={14} /> {busy ? "SENDING…" : "SUBMIT"}
        </button>
      </div>
    </Card>

    <Card title="My leave requests" testid="staff-leave-list">
      {rows.length === 0 ? <div style={{ color: "var(--sm-muted)", fontSize: 13 }}>No requests yet.</div>
        : rows.map((l) => {
          const s = (l.status || "").toLowerCase();
          const cls = s === "approved" ? "sm-chip-ok" : s === "rejected" ? "sm-chip-late" : "sm-chip-warn";
          return <div key={l.id} className="sm-row">
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>{l.leave_type_name}</div>
              <div style={{ fontSize: 12, color: "var(--sm-muted)" }}>{l.date_from} → {l.date_to} · {l.days}d</div>
            </div>
            <span className={`sm-chip ${cls}`}>{(l.status || "").toUpperCase()}</span>
          </div>;
        })}
    </Card>
  </>);
}

function LatenessAbsence({ me }) {
  const [d, setD] = useState({ records: [], corrections_used: 0, corrections_limit: 3 });
  const [reason, setReason] = useState("");
  const [selDate, setSelDate] = useState("");
  const [absDate, setAbsDate] = useState("");
  const [absReason, setAbsReason] = useState("");
  const [lnDate, setLnDate] = useState("");
  const [lnTime, setLnTime] = useState("");
  const [lnReason, setLnReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => staffApi.get("/staff/lateness").then(({ data }) => setD(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const tomorrowStr = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  const submitCorrection = async () => {
    if (!selDate || !reason) return toast.error("Pick a date and reason");
    try {
      await staffApi.post("/hr/lateness-corrections/request", { employee_id: me.employee_id, date: selDate, reason });
      toast.success("Correction request submitted"); setReason(""); setSelDate(""); load();
    } catch (e) { toast.error(staffErr(e)); }
  };
  const submitPreLate = async () => {
    if (!lnDate || !lnReason) return toast.error("Fill date and reason");
    setBusy(true);
    try {
      await staffApi.post("/hr/lateness/pre-notify", { employee_id: me.employee_id, reason: lnReason, expected_arrival: lnTime, for_date: lnDate });
      toast.success("Pre-notification sent to your supervisor");
      setLnDate(""); setLnTime(""); setLnReason("");
    } catch (e) { toast.error(staffErr(e)); } finally { setBusy(false); }
  };
  const submitPreAbsent = async () => {
    if (!absDate || !absReason) return toast.error("Fill date and reason");
    setBusy(true);
    try {
      await staffApi.post("/hr/absence/pre-notify", { employee_id: me.employee_id, for_date: absDate, reason: absReason });
      toast.success("Absence pre-notification sent"); setAbsDate(""); setAbsReason("");
    } catch (e) { toast.error(staffErr(e)); } finally { setBusy(false); }
  };

  return (<>
    <Card title="Notify supervisor in advance" hint="Requires supervisor approval" testid="staff-prenotify">
      <div className="sm-grid sm-grid-2 sm-notify-split" style={{ gap: 12 }}>
        <div style={{ border: "1px solid var(--sm-border)", borderRadius: 12, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <AlertTriangle size={16} color="var(--sm-gold)" />
            <div style={{ fontWeight: 700 }}>I will be late</div>
          </div>
          <div className="sm-grid sm-grid-2" style={{ gap: 8 }}>
            <input className="sm-input" type="date" min={tomorrowStr} value={lnDate} onChange={(e) => setLnDate(e.target.value)} data-testid="pre-late-date" />
            <input className="sm-input" type="time" value={lnTime} onChange={(e) => setLnTime(e.target.value)} placeholder="ETA" data-testid="pre-late-time" />
          </div>
          <textarea className="sm-textarea" style={{ marginTop: 8 }} rows={2} value={lnReason} onChange={(e) => setLnReason(e.target.value)} placeholder="Reason" data-testid="pre-late-reason" />
          <button className="sm-btn sm-btn-gold" style={{ marginTop: 10 }} onClick={submitPreLate} disabled={busy || !lnDate || !lnReason} data-testid="pre-late-submit">SEND LATE NOTICE</button>
        </div>

        <div style={{ border: "1px solid var(--sm-border)", borderRadius: 12, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <UserX size={16} color="var(--sm-error)" />
            <div style={{ fontWeight: 700 }}>I won't be reporting</div>
          </div>
          <input className="sm-input" type="date" min={tomorrowStr} value={absDate} onChange={(e) => setAbsDate(e.target.value)} data-testid="pre-abs-date" />
          <textarea className="sm-textarea" style={{ marginTop: 8 }} rows={2} value={absReason} onChange={(e) => setAbsReason(e.target.value)} placeholder="Reason" data-testid="pre-abs-reason" />
          <button className="sm-btn sm-btn-danger" style={{ marginTop: 10 }} onClick={submitPreAbsent} disabled={busy || !absDate || !absReason} data-testid="pre-abs-submit">SEND ABSENCE NOTICE</button>
        </div>
      </div>
    </Card>

    <Card title={`Lateness this month`} hint={`${d.corrections_used}/${d.corrections_limit} corrections used`} testid="staff-lateness-summary">
      {d.records.length === 0 ? <div style={{ color: "var(--sm-muted)", fontSize: 13 }}>No lateness recorded this month.</div>
        : d.records.map((r) => (
          <label key={r.id} className="sm-row" style={{ cursor: "pointer" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <input type="radio" name="latedate" value={r.date} checked={selDate === r.date} onChange={() => setSelDate(r.date)} data-testid={`ln-select-${r.date}`} style={{ width: 20, height: 20 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{r.date}</div>
                <div style={{ fontSize: 11, color: "var(--sm-muted)" }}>Clock-in {r.clock_in || "—"} · Late {r.late_minutes || 0}m</div>
              </div>
            </div>
            <span className="sm-chip sm-chip-late">LATE</span>
          </label>
        ))}
    </Card>

    <Card title="Submit correction request" hint="Pick a late day above" testid="staff-lateness-correction">
      <textarea className="sm-textarea" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (e.g. traffic accident, medical emergency)…" data-testid="ln-reason" />
      <button className="sm-btn sm-btn-primary" style={{ marginTop: 10 }}
              onClick={submitCorrection} disabled={!selDate || !reason || d.corrections_used >= d.corrections_limit} data-testid="ln-submit">
        {d.corrections_used >= d.corrections_limit ? "MONTHLY LIMIT REACHED" : "SUBMIT CORRECTION"}
      </button>
    </Card>
  </>);
}

function CreditSection() {
  const [d, setD] = useState({ balance: 0, transactions: [] });
  useEffect(() => { staffApi.get("/staff/credits").then(({ data }) => setD(data)).catch(() => {}); }, []);
  const charges = d.transactions.filter((t) => t.type === "charge");
  const settled = d.transactions.filter((t) => t.status === "settled");
  return (<>
    <div className="sm-grid sm-grid-3" style={{ marginBottom: 16 }}>
      <Stat label="Outstanding credit" value={N(d.balance)} accent="var(--sm-crimson)" />
      <Stat label="Total charges" value={charges.length} accent="var(--sm-gold)" />
      <Stat label="Settled from payroll" value={settled.length} accent="var(--sm-success)" />
    </div>

    <Card title="How this works" testid="staff-credit-explainer">
      <div style={{ fontSize: 13, color: "var(--sm-muted)", lineHeight: 1.6 }}>
        When you buy at the counter on staff credit, each order shows here. On payday, HR sweeps your outstanding balance into your payslip so it settles automatically.
      </div>
    </Card>

    <Card title={`Credit ledger · ${d.transactions.length} entries`} testid="staff-credit-ledger">
      {d.transactions.length === 0 ? <div style={{ color: "var(--sm-muted)", fontSize: 13 }}>No credit transactions yet.</div>
        : d.transactions.map((t) => {
          const isCharge = t.type === "charge";
          const swept = t.status === "settled" || t.type === "repayment";
          return (
            <div key={t.id} className="sm-row" data-testid={`credit-row-${t.id}`}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{isCharge ? (t.order_number || "Credit purchase") : (t.source === "payroll_conversion" ? "Payroll settlement" : "Repayment")}</div>
                <div style={{ fontSize: 11, color: "var(--sm-muted)", marginTop: 2 }}>
                  {new Date(t.created_at).toLocaleString("en-NG")}
                  {swept && isCharge && <span className="sm-chip sm-chip-ok" style={{ marginLeft: 8 }}>SETTLED</span>}
                  {isCharge && !swept && <span className="sm-chip sm-chip-warn" style={{ marginLeft: 8 }}>OUTSTANDING</span>}
                </div>
              </div>
              <div style={{ fontWeight: 800, fontSize: 15, color: isCharge ? "var(--sm-error)" : "var(--sm-success)", flex: "0 0 auto" }}>
                {isCharge ? "+" : "−"}{N(t.amount)}
              </div>
            </div>
          );
        })}
    </Card>
  </>);
}

function QueriesSection() {
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [box, setBox] = useState("inbox");
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const load = () => {
    staffApi.get("/staff/queries", { params: { box: "inbox" } }).then(({ data }) => setInbox(data));
    staffApi.get("/staff/queries", { params: { box: "sent" } }).then(({ data }) => setSent(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const rows = box === "inbox" ? inbox : sent;
  const sendReply = async () => {
    if (!reply || !open) return;
    try { const { data } = await staffApi.post(`/hr/queries/${open.id}/reply`, { body: reply }); setOpen(data); setReply(""); load(); }
    catch (e) { toast.error(staffErr(e)); }
  };
  return (<>
    <Card title="My queries" testid="staff-query-list"
      hint="Queries are raised by your supervisor or HR — open one to read it and reply"
      action={<div style={{ display: "flex", gap: 6 }}>{["inbox", "sent"].map((b) => (
        <button key={b} onClick={() => setBox(b)} data-testid={`q-box-${b}`}
                className={`sm-tab ${box === b ? "active" : ""}`} style={{ padding: "6px 12px", fontSize: 11 }}>{b.toUpperCase()}</button>
      ))}</div>}>
      {rows.length === 0 ? <div style={{ color: "var(--sm-muted)", fontSize: 13 }}>No {box} queries.</div>
        : rows.map((q) => (
          <button key={q.id} onClick={() => setOpen(q)} data-testid={`q-open-${q.id}`}
                  style={{ display: "block", textAlign: "left", width: "100%", padding: "12px 0", borderTop: "1px dashed var(--sm-border)", background: "transparent", border: "none", cursor: "pointer" }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
              {q.severity && <span className="sm-chip" style={{ background: q.severity === "Final warning" ? "#F7E5E5" : q.severity === "Warning" ? "#FBF0DE" : "#F3ECE2", color: q.severity === "Final warning" ? "#A52222" : q.severity === "Warning" ? "#B7791F" : "#765A4A" }}>{q.severity.toUpperCase()}</span>}
              {q.category && <span className="sm-chip" style={{ background: "#F3ECE2", color: "#765A4A" }}>{q.category.toUpperCase()}</span>}
              {q.due_by && <span className="sm-chip" style={{ background: "#FBF0DE", color: "#B7791F" }}>RESPOND BY {q.due_by}</span>}
            </div>
            <div style={{ fontWeight: 700 }}>{q.subject}</div>
            <div style={{ fontSize: 11, color: "var(--sm-muted)" }}>{box === "inbox" ? `From ${q.from_name}` : `To ${q.to_name}`} · {(q.messages || []).length} message(s) · {q.status}</div>
          </button>
        ))}
    </Card>

    {open && (
      <div className="sm-overlay" onClick={() => setOpen(null)} data-testid="q-thread-modal">
        <div className="sm-modal" onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10 }}>
            <div style={{ fontWeight: 800, color: "var(--sm-crimson)", fontSize: 16, minWidth: 0, wordBreak: "break-word" }}>{open.subject}</div>
            <button onClick={() => setOpen(null)} className="sm-icon-btn" aria-label="Close"><X size={16} /></button>
          </div>
          <div style={{ fontSize: 12, color: "var(--sm-muted)", marginBottom: 8 }}>From {open.from_name} → {open.to_name}</div>
          <div style={{ flex: 1, overflowY: "auto", padding: "8px 0", borderTop: "1px solid var(--sm-border)" }}>
            {(open.messages || []).map((m) => (
              <div key={m.id} style={{ padding: "10px 0", borderBottom: "1px dashed var(--sm-border)" }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{m.author_name} <span style={{ color: "var(--sm-muted)", fontWeight: 400, fontSize: 11 }}>· {new Date(m.at).toLocaleString("en-NG")}</span></div>
                <div style={{ marginTop: 4, fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.body}</div>
              </div>
            ))}
          </div>
          <textarea className="sm-textarea" style={{ marginTop: 10 }} value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
                    placeholder={["resolved", "escalated"].includes(open.status) ? "This query is closed" : "Reply…"}
                    disabled={["resolved", "escalated"].includes(open.status)} data-testid="q-reply" />
          <button className="sm-btn sm-btn-primary" style={{ marginTop: 8 }} onClick={sendReply}
                  disabled={["resolved", "escalated"].includes(open.status)} data-testid="q-reply-send">
            {["resolved", "escalated"].includes(open.status) ? `${open.status.toUpperCase()} · ${open.outcome_note || ""}` : "REPLY"}
          </button>
        </div>
      </div>
    )}
  </>);
}

function NotifPanel({ notifs, unread, onClose, onMarkOne, onMarkAll }) {
  return (
    <div className="sm-sidepanel" onClick={onClose} data-testid="staff-notifs-panel">
      <div className="sm-sidepanel-inner" onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--sm-border)", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "var(--sm-surf)", zIndex: 5 }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>Notifications</div>
          <div style={{ display: "flex", gap: 8 }}>
            {unread > 0 && <button onClick={onMarkAll} data-testid="staff-mark-all-read" style={{ background: "transparent", border: "none", color: "var(--sm-crimson)", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Mark all</button>}
            <button className="sm-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
          </div>
        </div>
        {notifs.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--sm-muted)", fontSize: 13 }} data-testid="staff-notifs-empty">You're all caught up.</div>
        ) : notifs.map((n) => {
          const ev = (n.event || "").toLowerCase();
          const clr = ev.includes("reject") ? "var(--sm-error)" : ev.includes("approve") ? "var(--sm-success)" : "var(--sm-gold)";
          return (
            <button key={n.id} onClick={() => onMarkOne(n.id)} data-testid={`staff-notif-${n.id}`}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "14px 22px", borderBottom: "1px solid var(--sm-border)", background: n.read ? "#fff" : "#FFFCF7", border: "none", borderLeft: `3px solid ${n.read ? "transparent" : clr}`, cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 700, fontSize: 11, color: clr, textTransform: "uppercase" }}>{n.workflow_type} · {n.event}</span>
                <span style={{ fontSize: 10, color: "var(--sm-muted)" }}>{new Date(n.created_at).toLocaleString("en-NG")}</span>
              </div>
              <div style={{ fontSize: 13 }}>{n.message}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
